import React from 'react';
import styled from 'styled-components';
import { space } from 'styled-system';
import { bool, string, oneOf } from 'prop-types';
import { variant } from 'styled-system';

import { inputPropTypesObject } from '../elements/form/decorators';
import LabelMessage from './LabelMessage';

const StyledContainer = styled('label')(space);

const HiddenSwitch = styled('input')(({ theme }) => ({
  height: 0,
  width: 0,
  visibility: 'hidden',
  '&:checked + span': {
    background: theme.colors.main,
    '&:after': {
      left: 'calc(100% - 5px)',
      transform: 'translateX(-100%)'
    }
  }
}));

const StyledSwitch = styled('span')(({ theme }) => ({
  cursor: 'pointer',
  width: '36px',
  height: '20px',
  background: theme.colors.dark,
  display: 'inline-block',
  borderRadius: '100px',
  position: 'relative',
  verticalAlign: 'middle',
  '&:after': {
    content: '""',
    position: 'absolute',
    top: '4px',
    left: '4px',
    width: '12px',
    height: '12px',
    background: theme.colors.white,
    borderRadius: '90px',
    transition: '0.3s'
  },
  '&:active:after': {
    width: '20px'
  }
}));

const StyledLabel = styled('span')(
  ({ theme }) => ({
    marginLeft: theme.space[1],
    cursor: 'pointer',
    fontWeight: 600,
    verticalAlign: 'middle'
  }),
  ({ theme }) =>
    variant({
      prop: 'type',
      variants: {
        success: {
          color: theme.colors.blue
        },
        complete: {
          color: theme.colors.main
        },
        error: {
          color: theme.colors.red
        },
        warning: {
          color: theme.colors.secondary
        },
        default: {
          color: theme.colors.grey
        }
      }
    })
);

const Switch = React.forwardRef(
  ({ label, messageWithIcon, errorMessage, defaultValue, type, onChange, ...input }, ref) => {
    const hasError = !!errorMessage;
    return (
      <StyledContainer {...input} data-cy={`checkbox-${input.name}`}>
        <HiddenSwitch
          type="checkbox"
          defaultChecked={defaultValue}
          {...input}
          onChange={onChange}
          ref={ref}
          error={hasError}
        />
        <StyledSwitch />
        <StyledLabel type={type}>{label}</StyledLabel>
        {errorMessage && (
          <LabelMessage withIcon={messageWithIcon} type="error" inputName={input.name}>
            {errorMessage}
          </LabelMessage>
        )}
      </StyledContainer>
    );
  }
);

Switch.defaultProps = {
  label: '',
  errorMessage: '',
  messageWithIcon: false,
  type: ''
};

Switch.propTypes = {
  ...inputPropTypesObject,
  errorMessage: string,
  label: string.isRequired,
  messageWithIcon: bool,
  type: oneOf(['', 'success', 'complete', 'error', 'warning', 'default'])
};

export default Switch;
